import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { Link } from 'react-router-dom';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    setIsMobileMenuOpen(false);
    if (id === 'hero') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const navLinks = [
    { label: 'Home', id: 'hero' },
    { label: 'Roxxie', id: 'roxxie', isPage: true },
    { label: 'Services', id: 'services' },
    { label: 'Music', id: 'music' },
    { label: 'Pricing', id: 'pricing' },
    { label: 'Blog', id: 'blog', isPage: true },
    { label: 'Contact', id: 'contact' }
  ];

  return (
    <>
      <motion.nav
        data-testid="navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled 
            ? 'glass py-4' 
            : 'bg-transparent py-6'
        }`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
          {/* Logo */}
          <button 
            onClick={() => scrollToSection('hero')}
            className="font-syne font-extrabold text-xl tracking-tighter uppercase glitch-hover"
          >
            <span className="text-neon-red">Shadowcell</span> Studios
          </button>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              link.isPage ? (
                <Link
                  key={link.id}
                  to={`/${link.id}`}
                  data-testid={`nav-${link.id}`}
                  className="font-manrope font-medium text-sm tracking-widest uppercase text-neon-red hover:text-white transition-colors duration-300 animated-underline"
                >
                  {link.label}
                </Link>
              ) : (
                <button
                  key={link.id}
                  data-testid={`nav-${link.id}`}
                  onClick={() => scrollToSection(link.id)}
                  className="font-manrope font-medium text-sm tracking-widest uppercase text-neutral-300 hover:text-neon-red transition-colors duration-300 animated-underline"
                >
                  {link.label}
                </button>
              )
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden text-white p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-40 bg-void-black/95 backdrop-blur-xl flex items-center justify-center md:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex flex-col items-center gap-8">
              {navLinks.map((link, index) => (
                link.isPage ? (
                  <motion.div
                    key={link.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Link
                      to={`/${link.id}`}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="font-syne font-bold text-3xl uppercase text-neon-red hover:text-white transition-colors duration-300"
                    >
                      {link.label}
                    </Link>
                  </motion.div>
                ) : (
                  <motion.button
                    key={link.id}
                    onClick={() => scrollToSection(link.id)}
                    className="font-syne font-bold text-3xl uppercase text-white hover:text-neon-red transition-colors duration-300"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    {link.label}
                  </motion.button>
                )
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
